// src/components/documents/DocumentProfitCard.js — doced-8f3a61c2 (Document editor, Stage 5g)
//
// The Profit card under the totals (approved mockup, 2026-10-30; round 1 fix 2026-10-30): "Profit", "Not
// shown to customers, sales or technicians" (offcost-2e7c9a14), three stacked rows like the phone block — Cost, Profit (green), Margin — label left,
// value right, values wrap rather than cut off; amounts with a plain "$" (no "CA$"; this card only); a yellow
// note naming the lines with no cost, and "Based on today's catalog costs. Before tax."
//
// HARD RULE (Marcus, 2026-10-30): Owner / Admin / Manager — and Office Staff (offcost-2e7c9a14, Marcus
// 2026-10-30) — only, i.e. canUseCatalog (= the server's COST_ROLES). For any other role this renders NOTHING (not
// a hidden card) and the costs are never asked for — the editor doesn't mount it, and it checks the role
// itself as well. The figures live on screen only; nothing here is saved.

import React, { useMemo } from 'react';
import { canUseCatalog } from '../../utils/catalogUi'; // offcost-2e7c9a14
import { formatCurrency } from '../../utils/formatters';
import { computeEstimateProfit, profitServiceIds, noCostNote } from '../../utils/estimateProfit';
import useCatalogCosts from '../../hooks/useCatalogCosts';

// This card only: the currency's plain symbol ("$" for CAD and USD, never "CA$"); the rest of the app is unchanged.
export function profitMoney(n, currency) {
    const value = Number.isFinite(Number(n)) ? Number(n) : 0;
    try {
        return value.toLocaleString('en-US', { style: 'currency', currency: currency || 'USD', currencyDisplay: 'narrowSymbol' });
    } catch (_) {
        return formatCurrency(value, currency);
    }
}

export default function DocumentProfitCard({ role, lines, totalsLines, currency }) {
    const allowed = canUseCatalog(role);
    const ids = useMemo(() => (allowed ? profitServiceIds(lines) : []), [allowed, lines]);
    const { costs, loading, error, retry } = useCatalogCosts(ids, allowed);
    if (!allowed) return null;
    const p = computeEstimateProfit({ lines, totalsLines, costs });
    const waiting = loading || (p.pending > 0 && !error);
    const money = (n) => profitMoney(n, currency);
    const shown = p.counted > 0 && !waiting;
    const loss = shown && p.profit < 0;
    const row = 'flex items-baseline justify-between gap-3 min-h-[28px]';
    const value = 'text-[15px] font-bold text-right break-words min-w-0'; // wraps, never an ellipsis
    return (
        <section aria-label="Profit" data-testid="doced-profit-card" className="bg-white border border-gray-200 rounded-2xl p-5 flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <h2 className="text-[15px] font-bold text-gray-900">Profit</h2>
                <span className="text-xs text-gray-500">Not shown to customers, sales or technicians</span>
            </div>
            <dl className="flex flex-col gap-1 tabular-nums">
                <div className={row}>
                    <dt className="text-sm text-gray-600 shrink-0">Cost</dt>
                    <dd data-testid="doced-profit-cost" className={`${value} text-gray-900`}>{shown ? money(p.cost) : '—'}</dd>
                </div>
                <div className={row}>
                    <dt className="text-sm text-gray-600 shrink-0">Profit</dt>
                    <dd data-testid="doced-profit-profit" className={`${value} ${loss ? 'text-red-700' : 'text-green-700'}`}>{shown ? money(p.profit) : '—'}</dd>
                </div>
                <div className={row}>
                    <dt className="text-sm text-gray-600 shrink-0">Margin</dt>
                    <dd data-testid="doced-profit-margin" className={`${value} text-gray-900`}>{shown && p.marginPct !== null ? `${p.marginPct}%` : '—'}</dd>
                </div>
            </dl>
            {waiting && <p className="text-xs text-gray-500" aria-live="polite">Loading today’s catalog costs…</p>}
            {error && (
                <div className="flex flex-wrap items-center gap-2 text-xs text-red-700">
                    <span>Couldn’t load the catalog costs.</span>
                    <button type="button" onClick={retry} className="min-h-[44px] px-3 rounded-md border border-red-300 bg-white font-semibold">Try again</button>
                </div>
            )}
            {!waiting && p.noCost.length > 0 && (
                <p className="p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">{noCostNote(p.noCost)}</p>
            )}
            {!waiting && !error && p.counted === 0 && p.noCost.length === 0 && (
                <p className="text-xs text-gray-500">Add a line from the catalog to see its cost.</p>
            )}
            <p className="text-xs text-gray-500">Based on today’s catalog costs. Before tax.</p>
        </section>
    );
}
